import type { DataType, ValueAddPhase } from '@arcanum-hq/shared-types';
import {
  formatCurrency,
  formatNumber,
  formatPercent,
  formatMultiple,
  excelSerialToDate,
} from './format';

// Local DataType → format function dispatch. Mirrors the canonical 11-value
// DataType enum from packages/shared-types/src/lib/model.types.ts. Defaults
// to `String(value)` for the few exotic types (currency_per_sf, currency_per_unit_year)
// without a dedicated formatter today; expand as needed.
function formatByDataType(value: number, dataType: DataType): string {
  switch (dataType) {
    case 'currency':
      return formatCurrency(value);
    case 'percentage':
      return formatPercent(value);
    case 'decimal':
      return formatMultiple(value);
    case 'integer':
    case 'months':
    case 'years':
      return formatNumber(value);
    case 'date':
      return excelSerialToDate(value);
    default:
      // currency_per_sf / currency_per_unit_year / text / other — default to
      // currency for $-prefixed types, integer formatting otherwise. The
      // narrative tokens that hit these types are rare; templates should
      // prefer mappings that resolve to the simpler types.
      if (typeof dataType === 'string' && dataType.startsWith('currency')) {
        return formatCurrency(value);
      }
      return formatNumber(value);
  }
}

// deal-signal-overhaul Phase 4 Session 1 (2026-05-11).
//
// Render `{key}` template tokens in a value-add narrative string by resolving
// each token via the supplied resolver. Optional per-key dataType map drives
// numeric formatting; missing tokens or unresolved values render as `—`.
//
// Token grammar: `{[a-zA-Z][a-zA-Z0-9_]*}` — alphanumeric + underscore, must
// start with a letter. Matches `signalFieldMappings` key naming convention
// (e.g. `{snapshotUnits}`, `{comparisonAvgRent_inPlace}`). Unmatched tokens
// (no key found in the resolver) render as `—` to keep the narrative
// rendering robust against partial-mapping models.
//
// Pure function. No side effects. Tested in __tests__/value-add-narrative.spec.ts.

const TOKEN_RE = /\{([a-zA-Z][a-zA-Z0-9_]*)\}/g;

export interface RenderedPhase
  extends Omit<ValueAddPhase, 'narrative' | 'bullets'> {
  narrative: string;
  bullets: string[];
}

/**
 * Resolve a single template token to a display string. Returns `'—'` for any
 * null / undefined / non-finite-number resolver result.
 */
function renderToken(
  rawValue: unknown,
  dataType: DataType | undefined,
): string {
  if (rawValue == null) return '—';
  if (typeof rawValue === 'number' && !Number.isFinite(rawValue)) return '—';
  if (dataType && (typeof rawValue === 'number' || typeof rawValue === 'string')) {
    // Coerce string → number when a numeric dataType is supplied; defensive
    // for resolver outputs that arrive as serialized strings.
    const numeric =
      typeof rawValue === 'number' ? rawValue : Number(rawValue);
    if (!Number.isFinite(numeric)) return String(rawValue);
    return formatByDataType(numeric, dataType);
  }
  return String(rawValue);
}

/**
 * Render a single template string. Walks `{key}` tokens; resolves each via
 * `resolver(key)`; formats via `formatByKey[key]` if supplied. Unmatched
 * tokens render as `'—'`.
 */
export function renderValueAddNarrative(
  template: string,
  resolver: (key: string) => unknown,
  formatByKey?: Record<string, DataType>,
): string {
  return template.replace(TOKEN_RE, (_match, key: string) => {
    const value = resolver(key);
    return renderToken(value, formatByKey?.[key]);
  });
}

/**
 * Render an entire `ValueAddPhase` (narrative + bullets). Preserves the
 * `title` + `status` fields verbatim; the bullets array always renders as
 * `string[]` (empty when undefined on input). Used by `<ValueAddStory>` to
 * compose `<PhaseCard body>` content.
 */
export function renderValueAddPhase(
  phase: ValueAddPhase,
  resolver: (key: string) => unknown,
  formatByKey?: Record<string, DataType>,
): RenderedPhase {
  return {
    title: phase.title,
    status: phase.status,
    narrative: renderValueAddNarrative(phase.narrative, resolver, formatByKey),
    bullets: (phase.bullets ?? []).map((b) =>
      renderValueAddNarrative(b, resolver, formatByKey),
    ),
  };
}
